
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/sonner';
import { BellRing } from 'lucide-react';
import { addMinutes, addHours, addDays, format, isBefore } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Appointment, Pet } from '@/types/models';

interface NotificationsProps {
  appointments: Appointment[];
  pets: Pet[];
}

// Fonction pour vérifier si une notification doit être affichée
const shouldShowNotification = (appointment: Appointment): boolean => {
  if (!appointment.reminderEnabled) return false;
  
  const now = new Date();
  const appointmentDate = new Date(`${appointment.date}T${appointment.time || '00:00'}`);
  
  let notificationTime;
  if (appointment.notifyBefore && appointment.notifyUnit) {
    switch (appointment.notifyUnit) {
      case 'minutes':
        notificationTime = addMinutes(appointmentDate, -appointment.notifyBefore);
        break;
      case 'hours':
        notificationTime = addHours(appointmentDate, -appointment.notifyBefore);
        break;
      case 'days':
        notificationTime = addDays(appointmentDate, -appointment.notifyBefore);
        break;
      default:
        notificationTime = appointmentDate;
    }
  } else if (appointment.reminderTime) {
    const [hours, minutes] = appointment.reminderTime.split(':').map(Number);
    notificationTime = new Date(appointment.date);
    notificationTime.setHours(hours, minutes, 0);
  } else {
    notificationTime = addHours(appointmentDate, -1);
  }
  
  const fiveMinutesAgo = addMinutes(now, -5);
  return isBefore(notificationTime, now) && isBefore(fiveMinutesAgo, notificationTime);
};

// Fonction pour obtenir l'icône selon le type de rendez-vous
const getAppointmentIcon = (title: string) => {
  const titleLower = title.toLowerCase();
  if (titleLower.includes('vétérinaire') || titleLower.includes('vet')) return '🏥';
  if (titleLower.includes('vaccin')) return '💉';
  if (titleLower.includes('vermifuge') || titleLower.includes('médicament')) return '💊';
  if (titleLower.includes('toilettage')) return '✂️';
  if (titleLower.includes('dentaire')) return '🦷';
  if (titleLower.includes('nourriture') || titleLower.includes('repas')) return '🍽️';
  return '📅';
};

// Fonction pour obtenir la couleur selon le type
const getNotificationColor = (title: string) => {
  const titleLower = title.toLowerCase();
  if (titleLower.includes('urgent') || titleLower.includes('important')) return 'from-red-500 to-pink-500';
  if (titleLower.includes('vétérinaire')) return 'from-blue-500 to-cyan-500';
  if (titleLower.includes('vaccin')) return 'from-green-500 to-emerald-500';
  if (titleLower.includes('médicament')) return 'from-purple-500 to-violet-500';
  return 'from-petblue-500 to-petgreen-500';
};

const shownNotifications = new Set<string>();

const NotificationsHandler: React.FC<NotificationsProps> = ({ appointments, pets }) => {
  const navigate = useNavigate();
  const appointmentsRef = useRef(appointments);
  const petsRef = useRef(pets);

  // Keep refs up-to-date without restarting the interval
  appointmentsRef.current = appointments;
  petsRef.current = pets;

  useEffect(() => {
    const checkNotifications = () => {
      appointmentsRef.current.forEach(appointment => {
        if (appointment.isCompleted) return;

        if (shouldShowNotification(appointment) && !shownNotifications.has(appointment.id)) {
          const pet = petsRef.current.find(p => p.id === appointment.petId);
          const appointmentIcon = getAppointmentIcon(appointment.title);
          const colorClass = getNotificationColor(appointment.title);

          toast(
            <div className="flex items-start gap-3 p-2">
              <div className={`w-12 h-12 bg-gradient-to-r ${colorClass} rounded-full flex items-center justify-center animate-pulse`}>
                <BellRing className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">{appointmentIcon}</span>
                  <p className="font-bold text-lg text-gray-800">{appointment.title}</p>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-medium text-petblue-600">🐾 {pet?.name || 'Votre animal'}</span>
                  <span className="mx-1">•</span>
                  <span>{format(new Date(appointment.date), 'dd MMMM', { locale: fr })}</span>
                  {appointment.time && (
                    <>
                      <span className="mx-1">•</span>
                      <span className="font-medium">⏰ {appointment.time}</span>
                    </>
                  )}
                </p>
                {appointment.location && (
                  <p className="text-xs text-gray-500 bg-gray-50 rounded-full px-2 py-1 inline-block">
                    📍 {appointment.location}
                  </p>
                )}
              </div>
            </div>,
            {
              duration: 12000,
              className: `border-l-4 border-l-petblue-500 bg-gradient-to-r from-white to-petblue-50/30 shadow-lg`,
              action: {
                label: "Voir le calendrier 📅",
                onClick: () => navigate('/calendar'),
              }
            }
          );

          shownNotifications.add(appointment.id);
        }
      });
    };

    checkNotifications();
    const interval = setInterval(checkNotifications, 60000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // single interval for the lifetime of the component

  return null;
};

export default NotificationsHandler;
